import { Mic, Square } from "@tamagui/lucide-icons";
import { type MutableRefObject, useCallback, useEffect } from "react";
import { Alert } from "react-native";
import { getTokenValue, useTheme } from "tamagui";

import {
  useSendVoiceMessage,
  useSendVoiceTranscription,
} from "@/api/mutations/conversation";
import LoadingSpinner from "@/components/animated/LoadingSpinner";
import { Button } from "@/components/Button";
import { useVoiceRecording } from "@/hooks/useVoiceRecording";
import { captureException } from "@/sentry";
import { useConversationComposition } from "@/store/conversation";

interface VoiceRecordButtonProps {
  disabled?: boolean;
  onRecordingStateChange?: (isRecording: boolean) => void;
  onStreamChange?: (stream: MediaStream | null) => void;
  onStopAndSendRef?: MutableRefObject<(() => Promise<void>) | undefined>;
}

const VoiceRecordButton = ({
  disabled,
  onRecordingStateChange,
  onStreamChange,
  onStopAndSendRef,
}: VoiceRecordButtonProps) => {
  const theme = useTheme();
  const { sendVoiceMessage } = useSendVoiceMessage();
  const { sendVoiceTranscription } = useSendVoiceTranscription();
  const { setText, isTranscribing } = useConversationComposition();

  const {
    recordingState,
    startRecording,
    stopRecording,
    hasPermission,
    mediaStream,
  } = useVoiceRecording({
    onAutoStop: (audioUri) => {
      if (audioUri) {
        sendVoiceTranscription(audioUri);
      }
    },
  });

  useEffect(() => {
    onRecordingStateChange?.(recordingState === "recording");
  }, [recordingState, onRecordingStateChange]);

  useEffect(() => {
    onStreamChange?.(mediaStream);
  }, [mediaStream, onStreamChange]);

  const stopAndSend = useCallback(async () => {
    if (recordingState !== "recording") {
      return;
    }
    try {
      const audioUri = await stopRecording();
      if (audioUri) {
        sendVoiceMessage(audioUri);
      }
    } catch (error) {
      captureException("Failed to send voice message", {
        extra: {
          category: "voice",
          error,
        },
      });
      Alert.alert(
        "Recording error",
        "Failed to send voice message. Please try again.",
        [{ text: "OK" }],
      );
    }
  }, [recordingState, stopRecording, sendVoiceMessage]);

  useEffect(() => {
    if (onStopAndSendRef) {
      onStopAndSendRef.current = stopAndSend;
    }
    return () => {
      if (onStopAndSendRef) {
        onStopAndSendRef.current = undefined;
      }
    };
  }, [onStopAndSendRef, stopAndSend]);

  const handlePress = useCallback(async () => {
    if (recordingState === "idle") {
      try {
        setText("");
        await startRecording();
      } catch (error) {
        captureException("Failed to start recording", {
          extra: {
            category: "voice",
            error,
          },
        });
        if (hasPermission === false) {
          Alert.alert(
            "Microphone permission required",
            "Please enable microphone access in your device settings to record voice messages.",
            [{ text: "OK" }],
          );
        } else {
          Alert.alert(
            "Recording error",
            "Failed to start recording. Please try again.",
            [{ text: "OK" }],
          );
        }
      }
    } else if (recordingState === "recording") {
      try {
        const audioUri = await stopRecording();
        if (audioUri) {
          sendVoiceTranscription(audioUri);
        }
      } catch (error) {
        captureException("Failed to transcribe voice message", {
          extra: {
            category: "voice",
            error,
          },
        });
        Alert.alert(
          "Recording error",
          "Failed to transcribe voice message. Please try again.",
          [{ text: "OK" }],
        );
      }
    }
  }, [
    recordingState,
    startRecording,
    stopRecording,
    sendVoiceTranscription,
    hasPermission,
    setText,
  ]);

  const isRecording = recordingState === "recording";
  const isProcessing = recordingState === "processing";
  const isDisabled = disabled || isProcessing;
  const iconSize = getTokenValue("$250");

  return (
    <Button.Wrapper
      onPress={handlePress}
      aria-label={isRecording ? "Stop recording" : "Record voice message"}
      borderRadius="$100"
      backgroundColor="$fillButtonPrimary"
      disabled={isDisabled}
      opacity={isDisabled ? 0.5 : 1}
      padding="$50"
      marginVertical="$100"
      round
    >
      {isTranscribing ? (
        <LoadingSpinner size={iconSize} />
      ) : isRecording ? (
        <Square
          size={iconSize}
          color="$iconCta"
          // Fill is a passthrough to the underlying SVG, so we retrieve the color from the theme first
          fill={theme.$iconCta?.get()}
          flexShrink={0}
        />
      ) : (
        <Mic size={iconSize} color="$iconCta" flexShrink={0} />
      )}
    </Button.Wrapper>
  );
};

export default VoiceRecordButton;
